import type { HelpTopicDetail } from './help-topic-detail.types';
import { HELP_TOPIC_DETAILS } from './help-topic-details.data';
import { HELP_TOPIC_DETAILS_BY_LANG } from './help-topic-details-locales';
import { UI_LANGUAGES, parseSupportedUiLangCode, type UiLanguageCode } from '@/lib/ui-languages';

export type HelpModalStored = {
  highlights: { label: string; value: string }[];
  steps: { title: string; description: string }[];
  sections: { heading: string; paragraphs: string[]; bullets: string[] }[];
  tips: string[];
};

/** Champs texte du formulaire admin (paragraphes et puces saisis en blocs). */
export type HelpModalDraft = {
  highlights: { label: string; value: string }[];
  steps: { title: string; description: string }[];
  sections: { heading: string; paragraphs: string; bullets: string }[];
  tips: string;
};

export function emptyModalDraft(): HelpModalDraft {
  return { highlights: [], steps: [], sections: [], tips: '' };
}

export function blankModalDraftMap(): Record<UiLanguageCode, HelpModalDraft> {
  return Object.fromEntries(UI_LANGUAGES.map((lang) => [lang.code, emptyModalDraft()])) as Record<
    UiLanguageCode,
    HelpModalDraft
  >;
}

export function modalDraftHasContent(draft: HelpModalDraft): boolean {
  if (draft.tips.trim()) return true;
  if (draft.highlights.some((row) => row.label.trim() || row.value.trim())) return true;
  if (draft.steps.some((row) => row.title.trim() || row.description.trim())) return true;
  return draft.sections.some((row) => row.heading.trim() || row.paragraphs.trim() || row.bullets.trim());
}

function linesOf(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function paragraphsOf(value: string): string[] {
  return value
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
}

export function storedFromDraft(draft: HelpModalDraft): HelpModalStored | null {
  const highlights = draft.highlights
    .map((row) => ({ label: row.label.trim(), value: row.value.trim() }))
    .filter((row) => row.label || row.value);
  const steps = draft.steps
    .map((row) => ({ title: row.title.trim(), description: row.description.trim() }))
    .filter((row) => row.title || row.description);
  const sections = draft.sections
    .map((row) => ({
      heading: row.heading.trim(),
      paragraphs: paragraphsOf(row.paragraphs),
      bullets: linesOf(row.bullets),
    }))
    .filter((row) => row.heading || row.paragraphs.length > 0 || row.bullets.length > 0);
  const tips = linesOf(draft.tips);
  if (!highlights.length && !steps.length && !sections.length && !tips.length) return null;
  return { highlights, steps, sections, tips };
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

export function parseStoredModal(value: unknown): HelpModalStored | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const highlights = Array.isArray(raw.highlights)
    ? raw.highlights
        .map((row) => {
          if (!row || typeof row !== 'object') return null;
          const item = row as Record<string, unknown>;
          const label = typeof item.label === 'string' ? item.label : '';
          const text = typeof item.value === 'string' ? item.value : '';
          if (!label.trim() && !text.trim()) return null;
          return { label, value: text };
        })
        .filter((row): row is { label: string; value: string } => row !== null)
    : [];
  const steps = Array.isArray(raw.steps)
    ? raw.steps
        .map((row) => {
          if (!row || typeof row !== 'object') return null;
          const item = row as Record<string, unknown>;
          const title = typeof item.title === 'string' ? item.title : '';
          const description = typeof item.description === 'string' ? item.description : '';
          if (!title.trim() && !description.trim()) return null;
          return { title, description };
        })
        .filter((row): row is { title: string; description: string } => row !== null)
    : [];
  const sections = Array.isArray(raw.sections)
    ? raw.sections
        .map((row) => {
          if (!row || typeof row !== 'object') return null;
          const item = row as Record<string, unknown>;
          const heading = typeof item.heading === 'string' ? item.heading : '';
          const paragraphs = asStringList(item.paragraphs);
          const bullets = asStringList(item.bullets);
          if (!heading.trim() && paragraphs.length === 0 && bullets.length === 0) return null;
          return { heading, paragraphs, bullets };
        })
        .filter((row): row is HelpModalStored['sections'][number] => row !== null)
    : [];
  const tips = asStringList(raw.tips).map((tip) => tip.trim()).filter(Boolean);
  if (!highlights.length && !steps.length && !sections.length && !tips.length) return null;
  return { highlights, steps, sections, tips };
}

export function draftFromStored(stored: HelpModalStored): HelpModalDraft {
  return {
    highlights: stored.highlights.map((row) => ({ ...row })),
    steps: stored.steps.map((row) => ({ ...row })),
    sections: stored.sections.map((row) => ({
      heading: row.heading,
      paragraphs: row.paragraphs.join('\n\n'),
      bullets: row.bullets.join('\n'),
    })),
    tips: stored.tips.join('\n'),
  };
}

export function draftFromDetail(detail: HelpTopicDetail): HelpModalDraft {
  return draftFromStored({
    highlights: detail.highlights,
    steps: detail.steps,
    sections: detail.sections.map((section) => ({
      heading: section.heading,
      paragraphs: section.paragraphs,
      bullets: section.bullets ?? [],
    })),
    tips: detail.tips,
  });
}

export function modalDraftsFromStored(
  detailI18n: unknown,
  legacyTopicId: string,
): Record<UiLanguageCode, HelpModalDraft> {
  const drafts = blankModalDraftMap();
  const raw =
    detailI18n && typeof detailI18n === 'object' && !Array.isArray(detailI18n)
      ? (detailI18n as Record<string, unknown>)
      : {};
  for (const lang of UI_LANGUAGES) {
    const stored = parseStoredModal(raw[lang.code]);
    if (stored) drafts[lang.code] = draftFromStored(stored);
  }
  if (!modalDraftHasContent(drafts.fr)) {
    const bundled = resolveBundledHelpTopicDetail(legacyTopicId.trim(), 'fr');
    if (bundled) drafts.fr = draftFromDetail(bundled);
  }
  return drafts;
}

export function compactModalDraftMap(
  drafts: Record<UiLanguageCode, HelpModalDraft>,
): Record<string, HelpModalStored> {
  const out: Record<string, HelpModalStored> = {};
  for (const lang of UI_LANGUAGES) {
    const stored = storedFromDraft(drafts[lang.code] ?? emptyModalDraft());
    if (stored) out[lang.code] = stored;
  }
  return out;
}

export function storedToDetail(stored: HelpModalStored): HelpTopicDetail {
  return {
    summary: '',
    highlights: stored.highlights,
    steps: stored.steps,
    sections: stored.sections.map((section) => ({
      heading: section.heading,
      paragraphs: section.paragraphs,
      bullets: section.bullets,
    })),
    tips: stored.tips,
    resources: [],
    quiz: [],
    relatedTopicIds: [],
  };
}

/** Langue demandée, sinon anglais, sinon français. Null si aucune langue n’a de corps enregistré. */
export function resolveStoredModalDetail(detailI18n: unknown, lang: string | undefined): HelpTopicDetail | null {
  const raw =
    detailI18n && typeof detailI18n === 'object' && !Array.isArray(detailI18n)
      ? (detailI18n as Record<string, unknown>)
      : {};
  const code = parseSupportedUiLangCode(lang) ?? 'en';
  const order: UiLanguageCode[] =
    code === 'en' ? ['en', 'fr'] : code === 'fr' ? ['fr', 'en'] : [code, 'en', 'fr'];
  for (const key of order) {
    const stored = parseStoredModal(raw[key]);
    if (stored) return storedToDetail(stored);
  }
  return null;
}

/**
 * Fiche bundlée dans le frontend : langue UI → anglais → français.
 * Couvre le catalogue même si la base n’a pas encore de detail_i18n.
 */
export function resolveBundledHelpTopicDetail(
  topicId: string,
  lang: string | undefined,
): HelpTopicDetail | null {
  const id = topicId.trim();
  if (!id) return null;
  const code = parseSupportedUiLangCode(lang) ?? 'fr';
  const byLang = HELP_TOPIC_DETAILS_BY_LANG;
  const order: UiLanguageCode[] =
    code === 'en' ? ['en', 'fr'] : code === 'fr' ? ['fr', 'en'] : [code, 'en', 'fr'];
  for (const key of order) {
    const hit = byLang[key]?.[id];
    if (hit) return hit;
  }
  return HELP_TOPIC_DETAILS[id] ?? null;
}
